import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../services/firebase';
import { doc, getDoc } from 'firebase/firestore';
import NotificationDropdown from './NotificationDropdown';
import OnboardingTour from './OnboardingTour';
import UserManual from './UserManual';

const Layout = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const profileMenuRef = React.useRef(null);
  const [showOnboarding, setShowOnboarding] = useState(false); // Always false to disable onboarding popup
  const [showUserManual, setShowUserManual] = useState(false);
  const [checkingProfile, setCheckingProfile] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const { currentUser, userRole, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Handle window resize for responsive behavior
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      if (!mobile) {
        setMobileOpen(false); // Close mobile menu on desktop
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Disable body scroll when mobile menu is open
  useEffect(() => {
    if (isMobile && mobileOpen) {
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
    } else {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
    }

    // Cleanup on unmount
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
    };
  }, [isMobile, mobileOpen]);
  
  // Handle click outside of profile menu
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target)) {
        setProfileMenuOpen(false);
      }
    };

    // Retraso pequeño para evitar que el evento se active inmediatamente
    const timer = setTimeout(() => {
      if (profileMenuOpen) {
        document.addEventListener('mousedown', handleClickOutside, { passive: true });
        document.addEventListener('touchstart', handleClickOutside, { passive: true });
      }
    }, 100);
    
    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [profileMenuOpen]);

  // Check if client profile is complete - Onboarding disabled
  useEffect(() => {
    const checkClientProfile = async () => {
      if (!currentUser || userRole !== 'client') {
        setCheckingProfile(false);
        return;
      }

      try {
        const userRef = doc(db, 'users', currentUser.uid);
        const userDoc = await getDoc(userRef);
        
        // Even if profile is incomplete, we don't show onboarding
        // setShowOnboarding is always false now
        setShowOnboarding(false);
      } catch (error) {
        console.error('Error checking client profile:', error);
      } finally {
        setCheckingProfile(false);
      }
    };

    checkClientProfile();
  }, [currentUser, userRole]);

  const handleOnboardingComplete = () => {
    setShowOnboarding(false);
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Failed to log out:', error);
    }
  };

  const clientMenuItems = [
    { text: 'Dashboard', path: '/', icon: '📊', isImage: false, iconStyle: { fontSize: '1.5rem', color: '#4338ca' } },
    { text: 'My Appointments', path: '/appointments', icon: '📅', isImage: false, iconStyle: { fontSize: '1.5rem', color: '#4338ca' } },
    { text: 'My Estimates', path: '/estimates', icon: '💵', isImage: false, iconStyle: { fontSize: '1.5rem', color: '#059669' } },
    { text: 'Mis Reseñas', path: '/my-reviews', icon: '⭐', isImage: false, iconStyle: { fontSize: '1.5rem', color: '#eab308' } },
   
    { text: 'Support Tickets', path: '/support', icon: '🎫', isImage: false, iconStyle: { fontSize: '1.5rem', color: '#8b5cf6' } },
    { text: 'Notifications', path: '/notifications', icon: '🔔', isImage: false, iconStyle: { fontSize: '1.5rem', color: '#f59e0b' } },
    { text: 'Profile', path: '/profile', icon: '👤', isImage: false, iconStyle: { fontSize: '1.5rem', color: '#64748b' } },
    { text: 'User Manual', icon: '📚', isImage: false, iconStyle: { fontSize: '1.5rem', color: '#475569' }, action: () => setShowUserManual(true) }
  ];

  const adminMenuItems = [
    { text: 'Dashboard', path: '/admin', icon: '📊', isImage: false, iconStyle: { fontSize: '1.5rem', color: '#4338ca' } },
    { text: 'Estimates', path: '/admin/estimates', icon: '💵', isImage: false, iconStyle: { fontSize: '1.5rem', color: '#059669' } },
    { text: 'Appointments', path: '/admin/appointments', icon: '📅', isImage: false, iconStyle: { fontSize: '1.5rem', color: '#4338ca' } },
    { text: 'Calendar', path: '/admin/slots', icon: '📆', isImage: false, iconStyle: { fontSize: '1.5rem', color: '#3b82f6' } },
    { text: 'Customers', path: '/admin/users', icon: '👥', isImage: false, iconStyle: { fontSize: '1.5rem', color: '#64748b' } },
    { text: 'Admins', path: '/admin/manage-admins', icon: '🔑', isImage: false, iconStyle: { fontSize: '1.5rem', color: '#0284c7' } },
    { text: 'Support Tickets', path: '/admin/support', icon: '🎟', isImage: false, iconStyle: { fontSize: '1.5rem', color: '#8b5cf6' } },
    { text: 'My Trees', path: '/admin/tree-health', icon: '🌳', isImage: false, iconStyle: { fontSize: '1.5rem', color: '#15803d' } },
    { text: 'Analytics', path: '/admin/analytics', icon: '📈', isImage: false, iconStyle: { fontSize: '1.5rem', color: '#ef4444' } },
    { text: 'Seasonal Planning', path: '/admin/seasonal-planning', icon: '🍂', isImage: false, iconStyle: { fontSize: '1.5rem', color: '#d97706' } },
    { text: 'Profile', path: '/admin/profile', icon: '👤', isImage: false, iconStyle: { fontSize: '1.5rem', color: '#64748b' } },
    { text: 'User Manual', icon: '📚', isImage: false, iconStyle: { fontSize: '1.5rem', color: '#475569' }, action: () => setShowUserManual(true) }
  ];

  const menuItems = userRole === 'admin' ? adminMenuItems : clientMenuItems;

  return (
    <div className="layout">
      {/* Mobile Sidebar Overlay */}
      <div 
        className={`sidebar-overlay ${mobileOpen ? 'active' : ''}`}
        onClick={() => setMobileOpen(false)}
      />

      {/* Sidebar */}
      <div className={`sidebar ${mobileOpen ? 'open' : ''}`}>
        {/* Logo Section */}
        <div className="logo-section">
          <img 
            src="/logo.png" 
            alt="Paradise Landscaping" 
            className="logo"
          />
          <div className="company-name">Paradise Landscaping</div>
        </div>
        
        {/* Navigation Menu */}
        <nav className="nav-menu">
          {menuItems.map((item) => (
            <a
              key={item.text}
              href={item.path}
              className={`nav-item ${item.path && location.pathname === item.path ? 'active' : ''}`}
              onClick={(e) => {
                e.preventDefault();
                if (item.action) {
                  item.action();
                } else {
                  navigate(item.path);
                }
                if (isMobile) setMobileOpen(false);
              }}
            >
              <span className="nav-icon" style={item.iconStyle || {}}>
                {item.isImage ? (
                  <img 
                    src={item.icon} 
                    alt={item.text}
                  />
                ) : (
                  item.icon
                )}
              </span>
              <span className="nav-text">{item.text}</span>
            </a>
          ))}
        </nav>
      </div>

      {/* Main Content */}
      <div className="main-content" style={{
        maxWidth: isMobile ? '100vw' : undefined,
        width: isMobile ? '100%' : undefined,
        marginLeft: isMobile ? '0' : undefined,
        overflowX: 'hidden',
        boxSizing: 'border-box'
      }}>
        {/* Header */}
        <header className="header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <button 
              onClick={handleDrawerToggle}
              className="mobile-menu-toggle"
              style={{
                display: isMobile ? 'block' : 'none'
              }}
            >
              ☰
            </button>
            <div>
              <h1 className="heading-2" style={{ margin: 0, fontSize: '1.25rem' }}>
                {userRole === 'admin' ? 'Admin Dashboard' : 'Client Portal'}
              </h1>
              <p className="text-muted" style={{ margin: 0, fontSize: '0.75rem' }}>
                Welcome back, {currentUser?.displayName || currentUser?.email?.split('@')[0] || 'User'}
              </p>
            </div>
          </div>
          
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: isMobile ? '0.5rem' : '1rem',
            flexWrap: 'wrap'
          }}>
            
            {/* Notifications */}
            <NotificationDropdown />
            
            {/* Messages */}
           
            
            {/* User Profile */}
            <div ref={profileMenuRef} style={{ position: 'relative' }}>
              <button
                onClick={() => setProfileMenuOpen(!profileMenuOpen)}
                className="btn btn-secondary profile-dropdown-btn"
                style={{
                  background: 'rgba(255, 255, 255, 0.1)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  color: 'var(--gray-700)',
                  backdropFilter: 'blur(10px)',
                  padding: isMobile ? '0.5rem' : '0.5rem 1rem',
                  borderRadius: 'var(--radius-xl)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem',
                  transition: 'all var(--transition-fast)',
                  boxShadow: 'var(--shadow-sm)',
                  position: 'relative',
                  zIndex: 100,
                  minWidth: isMobile ? '40px' : 'auto',
                  minHeight: isMobile ? '40px' : 'auto'
                }}
              >
                <div style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  background: 'var(--gradient-primary)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '0.875rem',
                  fontWeight: '700',
                  color: 'white',
                  boxShadow: 'var(--shadow-sm)'
                }}>
                  {currentUser?.displayName?.charAt(0) || currentUser?.email?.charAt(0) || 'U'}
                </div>
                <div style={{ textAlign: 'left' }} className="user-info-desktop">
                  <div style={{ fontSize: '0.875rem', fontWeight: '600', color: 'var(--gray-800)' }}>
                    {currentUser?.displayName || currentUser?.email?.split('@')[0] || 'User'}
                  </div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--gray-500)', textTransform: 'capitalize' }}>
                    {userRole}
                  </div>
                </div>
                <span style={{ fontSize: '0.75rem', color: 'var(--gray-400)' }}>▼</span>
              </button>
              
              {profileMenuOpen && (
                <>
                {isMobile && (
                  <div 
                    className="profile-menu-overlay"
                    style={{
                      position: 'fixed',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: 'rgba(0, 0, 0, 0.5)',
                      backdropFilter: 'blur(3px)',
                      zIndex: 1040
                    }}
                    onClick={() => setProfileMenuOpen(false)}
                  />
                )}
                <div className="modal-content profile-dropdown-menu" style={{
                  position: 'fixed',
                  top: isMobile ? 'auto' : '100%',
                  bottom: isMobile ? '10px' : 'auto',
                  right: isMobile ? '10px' : 0,
                  background: 'white',
                  borderRadius: 'var(--radius-xl)',
                  boxShadow: 'var(--shadow-2xl)',
                  padding: '0.5rem',
                  minWidth: isMobile ? '280px' : '240px',
                  maxWidth: isMobile ? '95vw' : 'auto',
                  zIndex: 1000,
                  marginTop: isMobile ? 0 : '0.5rem',
                  border: '1px solid rgba(0, 0, 0, 0.05)',
                  backdropFilter: 'blur(20px)',
                  animation: 'fadeIn 0.2s ease-out'
                }}>
                  <div style={{ 
                    padding: '1rem 1.25rem', 
                    borderBottom: '1px solid var(--gray-100)',
                    background: 'var(--gray-50)',
                    borderRadius: 'var(--radius-lg) var(--radius-lg) 0 0'
                  }}>
                    <div style={{ fontWeight: '600', color: 'var(--gray-800)', fontSize: '0.875rem', marginBottom: '0.25rem' }}>
                      {currentUser?.displayName || currentUser?.email?.split('@')[0] || 'User'}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>
                      {currentUser?.email}
                    </div>
                    <div style={{ 
                      fontSize: '0.75rem', 
                      color: 'var(--primary-600)', 
                      textTransform: 'capitalize',
                      fontWeight: '500',
                      marginTop: '0.25rem'
                    }}>
                      {userRole} Account
                    </div>
                  </div>
                  <div style={{ padding: '0.5rem' }}>
                    <button
                      onClick={() => {
                        navigate(userRole === 'admin' ? '/admin/profile' : '/profile');
                        setProfileMenuOpen(false);
                      }}
                      className="btn btn-secondary profile-action-btn"
                      style={{
                        width: '100%',
                        padding: isMobile ? '0.875rem 1rem' : '0.75rem 1rem',
                        background: 'none',
                        border: 'none',
                        textAlign: 'left',
                        cursor: 'pointer',
                        fontSize: isMobile ? '0.95rem' : '0.875rem',
                        fontWeight: isMobile ? '500' : 'normal',
                        color: 'var(--gray-700)',
                        borderRadius: 'var(--radius-lg)',
                        transition: 'all var(--transition-fast)',
                        marginBottom: '0.5rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.75rem'
                      }}
                    >
                      <span style={{ fontSize: '1.25rem' }}>👤</span>
                      Mi Perfil
                    </button>
                    <button
                      onClick={handleLogout}
                      className="btn profile-action-btn"
                      style={{
                        width: '100%',
                        padding: isMobile ? '0.875rem 1rem' : '0.75rem 1rem',
                        background: 'rgba(220, 38, 38, 0.08)',
                        border: 'none',
                        textAlign: 'left',
                        cursor: 'pointer',
                        fontSize: isMobile ? '0.95rem' : '0.875rem',
                        fontWeight: isMobile ? '600' : '500',
                        color: 'rgb(220, 38, 38)',
                        borderRadius: 'var(--radius-lg)',
                        transition: 'all var(--transition-fast)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.75rem'
                      }}
                    >
                      <span style={{ fontSize: '1.25rem' }}>🚪</span>
                      Cerrar Sesión
                    </button>
                  </div>
                </div>
                </>
              )}
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="content" style={{ 
          flex: 1, 
          overflow: 'auto',
          padding: 0,
          height: 'calc(100vh - 80px)', // Subtract header height
          maxWidth: '100%',
          width: '100%',
          overflowX: 'hidden',
          boxSizing: 'border-box'
        }}>
          {checkingProfile ? (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '200px',
              fontSize: '16px',
              color: '#666'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}>
                <div style={{
                  width: '20px',
                  height: '20px',
                  border: '2px solid #f3f3f3',
                  borderTop: '2px solid #4caf50',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }}></div>
                Loading your profile...
              </div>
            </div>
          ) : (
            children
          )}
        </main>
      </div>
      
      {/* Onboarding Tour for Clients - Disabled */}
      {false && userRole === 'client' && (
        <OnboardingTour onComplete={handleOnboardingComplete} />
      )}
      
      {/* User Manual Modal */}
      {showUserManual && (
        <UserManual isOpen={showUserManual} onClose={() => setShowUserManual(false)} />
      )}
      
      {/* CSS for loading spinner */}
      <style jsx>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default Layout;